import type { ColumnsType, ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { Feature, Point } from 'geojson';
import type { TFunction } from 'i18next';
import type {
  FlightStagePredictions,
  RegionalOverviewDigitalTrapsProperties
} from 'querys/regional-overview/regional-overview.query.models';
import DisplayTrapNameId from '../components/display-trap-name-id';
import FlightStageTag from '../components/flight-stage-tag/flight-stage-tag';
import type { TrapGroup } from '../domain';
import type { ColumnPrediction, FlightPredictionRow, FlightPredictionRowWithExtras } from '../domain/flight-prediction.models';
import type { FlightRealizationEnum } from '../domain/flight-stage.enum';
import { generateFlightPredictionColumnsAndKeys, renderSevenDayTotalCell } from './daily-counts.utils';

const mapPredictionsToColumns = (predictions: FlightStagePredictions[], dateKeys: string[]): (ColumnPrediction | undefined)[] => {
  const columnPredictions: (ColumnPrediction | undefined)[] = Array.from({ length: dateKeys.length }, () => undefined);
  if (dateKeys.length === 0) return columnPredictions;

  const windowStart = dayjs(dateKeys[0]);
  const windowEnd = dayjs(dateKeys.at(-1));

  predictions.forEach(prediction => {
    const predictedStart = dayjs(prediction.start_date);
    const predictedEnd = dayjs(prediction.end_date);

    if (predictedEnd.isBefore(windowStart, 'day') || predictedStart.isAfter(windowEnd, 'day')) return;

    const visibleStart = predictedStart.isBefore(windowStart, 'day') ? windowStart : predictedStart;
    const visibleEnd = predictedEnd.isAfter(windowEnd, 'day') ? windowEnd : predictedEnd;

    const startIndex = dateKeys.findIndex(dateKey => dayjs(dateKey).isSame(visibleStart, 'day'));
    if (startIndex === -1) return;

    const colSpan = visibleEnd.diff(visibleStart, 'day') + 1;
    columnPredictions[startIndex] = { prediction, colSpan };
  });

  return columnPredictions;
};

export const createFlightPredictionColumns = <
  T extends Record<string, string | number | boolean | (ColumnPrediction | undefined)[]> = FlightPredictionRow
>(
  dateTableColumns: ColumnsType<T>,
  columnPredictions?: ColumnPrediction[],
  showPlaceholder = true
): ColumnsType<T> => {
  return dateTableColumns.map((column: ColumnType<T>, index: number) => ({
    ...column,
    width: '6.66%',
    render: (_: unknown, record: T & { _columnPredictions?: ColumnPrediction[]; _hasPredictions?: boolean }) => {
      const predictionData = columnPredictions ? columnPredictions[index] : record._columnPredictions?.[index];

      if (predictionData) {
        return {
          children: (
            <FlightStageTag
              flightGeneration={predictionData.prediction.generation}
              flightStage={predictionData.prediction.stage as FlightRealizationEnum}
            />
          ),
          props: { colSpan: predictionData.colSpan }
        };
      }

      if (showPlaceholder && !record._hasPredictions) {
        return { children: '--', props: { colSpan: 1 } };
      }

      return { children: null, props: { colSpan: 0 } };
    }
  }));
};

const initializeRowWithDateKeys = <T extends Record<string, string | number | boolean | (ColumnPrediction | undefined)[]>>(
  rowData: T,
  dateKeys: string[],
  defaultValue: string | number = '--'
): T => {
  dateKeys.forEach(dateKey => {
    (rowData as Record<string, string | number>)[dateKey] = defaultValue;
  });
  return rowData;
};

export const processSingleTrapFlightPredictions = (trap: Feature<Point, RegionalOverviewDigitalTrapsProperties>, dateKeys: string[]) => {
  if (!trap.properties.flight_predictions?.predictions) {
    return {
      columnPredictions: Array.from({ length: dateKeys.length }, () => null),
      rowData: {
        id: '1',
        name: 'Flight Predictions',
        stage: 'Predictions'
      }
    };
  }

  const predictions = trap.properties.flight_predictions.predictions;
  const columnPredictions = mapPredictionsToColumns(predictions, dateKeys);

  const rowData: FlightPredictionRow = {
    id: '1',
    name: 'Flight Predictions',
    stage: 'Predictions'
  };

  initializeRowWithDateKeys(rowData, dateKeys, '');

  return { columnPredictions, rowData };
};

export const processMultipleTrapFlightPredictions = (trapGroups: TrapGroup[], t: TFunction) => {
  const { dateKeys, dateTableColumns } = generateFlightPredictionColumnsAndKeys({ t });

  return trapGroups.map(group => {
    const tableDataSource = group.trapsInGroup.map(trap => {
      const predictions = trap.properties.flight_predictions?.predictions ?? [];
      const columnPredictions = mapPredictionsToColumns(predictions, dateKeys);

      const rowData: FlightPredictionRowWithExtras = {
        id: trap.properties.id,
        name: trap.properties.name,
        stage: 'Predictions',
        total: trap.properties.total_number_pests || 0,
        status: trap.properties.level,
        _columnPredictions: columnPredictions,
        _hasPredictions: predictions.length > 0
      };

      initializeRowWithDateKeys(rowData, dateKeys, '--');
      return rowData;
    });

    const enhancedDateColumns = createFlightPredictionColumns(dateTableColumns, undefined, true);

    const allColumns = [
      {
        title: 'Trap Name/ID',
        dataIndex: 'name',
        key: 'name',
        render: (text: string, record: { id: string }) => <DisplayTrapNameId trapName={text} trapId={record.id} />,
        fixed: 'left' as const,
        width: '30%',
        align: 'left' as const,
        ellipsis: true
      },
      {
        title: t('pages.regional_overview.digital_traps.7_days_total'),
        dataIndex: 'total',
        key: 'total',
        render: renderSevenDayTotalCell,
        width: '8%',
        align: 'left' as const
      },
      ...enhancedDateColumns
    ];

    return {
      title: group.title,
      key: group.id,
      tableColumns: allColumns,
      tableDataSource
    };
  });
};
